import { observer } from "mobx-react-lite";
import React, { useCallback, useContext, useMemo } from "react";

import { unique } from "@bps/common-utils";
import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { Stack, useTheme } from "@bps/fluent-ui";
import {
  CalendarEventStatus,
  CalendarEventType
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { AppointmentInformationModalContext } from "@modules/booking/screens/booking-calendar/components/appointment-dialog/components/appointment-form/appointment-information/AppointmentInformationModalContext.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { useInfiniteScroll } from "@ui-components/InfiniteScrollList/useInfiniteScroll.ts";

import { PrefAndInformationPivotTabTypes } from "./AppointmentInformationPivotTabs.types.ts";
import { AppointmentInformationTable } from "./AppointmentInformationTable.tsx";

interface AppointmentInformationPivotTabsProps {
  onDismiss: () => void;
  contactId: string;
  claimId: string | undefined;
  selectedTab: PrefAndInformationPivotTabTypes;
}

export const AppointmentInformationPivotTabs: React.FC<AppointmentInformationPivotTabsProps> =
  observer(({ onDismiss, contactId, claimId, selectedTab }) => {
    const { acc, booking, practice, core } = useStores();

    const helper = useContext(AppointmentInformationModalContext);
    const theme = useTheme();

    const getItems = useCallback(async () => {
      const nowIso = DateTime.now().toISO();

      const isPast = selectedTab === PrefAndInformationPivotTabTypes.Past;
      const isUpcoming =
        selectedTab === PrefAndInformationPivotTabTypes.Upcoming;

      const timeFilter = {
        endsAfter: isUpcoming ? nowIso : undefined,
        endTime: isPast ? nowIso : undefined
      };

      const filterAndSort = (
        calendarEvents: CalendarEvent[],
        sortDescending: boolean = isPast
      ) =>
        calendarEvents
          .filter(
            calendarEvent =>
              calendarEvent.type !== CalendarEventType.TemporaryReservation
          )
          .sort((a, b) =>
            compareDatesPredicate(
              a.startDateTime,
              b.startDateTime,
              sortDescending
            )
          );

      const returnResult = (calendarEvents: CalendarEvent[]) => {
        return {
          skip: 0,
          take: calendarEvents.length,
          results: calendarEvents
        };
      };

      if (claimId) {
        const claimAppointments = await acc.getClaimAppointments({
          claimIds: [claimId]
        });

        const result = await booking.getCalendarEvents({
          calendarEventIds: claimAppointments.map(
            ({ calendarEventId }) => calendarEventId
          ),
          ...timeFilter
        });

        const filteredAndSorted = filterAndSort(result.results);

        await Promise.all(
          filteredAndSorted.map(calendarEvent =>
            practice.getProvider(calendarEvent.userId)
          )
        );

        return returnResult(filteredAndSorted);
      }

      const results = await booking.getCalendarEvents({
        attendees: [contactId],
        statuses: [
          CalendarEventStatus.Confirmed,
          CalendarEventStatus.Cancelled
        ],
        ...timeFilter
      });

      const filteredAndSorted = filterAndSort(results.results);

      if (core.isNZTenant) {
        const calendarEventIds = filteredAndSorted.map(
          calendarEvent => calendarEvent.id
        );
        if (!!calendarEventIds.length) {
          const claimAppointments =
            await acc.getClaimAppointmentsByCalendarEventIds(calendarEventIds);

          const claimIds = claimAppointments.map(({ claimId }) => claimId);
          const uniqueClaimIds = unique(claimIds);

          if (uniqueClaimIds.length) {
            await acc.getClaims(uniqueClaimIds);
          }
        }
      }

      if (isUpcoming) {
        helper.setUpcomingAppointmentNumber(filteredAndSorted.length);
      }

      return returnResult(filteredAndSorted);
    }, [
      acc,
      booking,
      practice,
      contactId,
      claimId,
      core.isNZTenant,
      selectedTab,
      helper
    ]);

    const refreshKey = useMemo(() => {
      const calendarEventId = booking.ui.lastUpdatedCalendarEventData?.id;
      if (calendarEventId) {
        const calendarEvent = booking.calendarEventsMap.get(calendarEventId);
        if (
          calendarEvent?.attendees.some(
            attendee => attendee.attendeeId === contactId
          )
        ) {
          return calendarEvent.eTag;
        }
      }

      return "";
    }, [
      booking.calendarEventsMap,
      booking.ui.lastUpdatedCalendarEventData?.id,
      contactId
    ]);

    const { searchResults, isLoading } = useInfiniteScroll({
      getItems,
      refreshKey
    });

    const items = useMemo(
      () => searchResults.value?.results ?? [],
      [searchResults.value?.results]
    );

    return (
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: { height: "100%" } }}>
        <Stack
          styles={{
            root: {
              padding: 4,
              marginTop: 8,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: theme.palette.neutralLight
            }
          }}
        >
          <AppointmentInformationTable
            items={items}
            claimId={claimId}
            onDismiss={onDismiss}
            isLoading={isLoading}
            selectedTab={selectedTab}
          />
        </Stack>
      </Stack>
    );
  });
